import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Wallet,
  AlarmClock,
  Loader2,
  Ban,
  Pencil,
  Receipt,
  Ticket,
  Activity,
  User,
  AlertTriangle,
  CheckCircle2,
  PauseCircle,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, formatMXN } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { useCliente, useClienteResumen } from "./api";
import { ClienteGeneralTab } from "./ClienteGeneralTab";
import { ClienteFormDialog } from "./ClienteFormDialog";
import { DarDeBajaDialog } from "./DarDeBajaDialog";
import { ComingInPhaseTab } from "./ComingInPhaseTab";
import { PagosTable } from "@/features/pagos/PagosTable";
import { ClienteContratosTab } from "@/features/contratos/ClienteContratosTab";
import { ClienteFacturasVencidasSection } from "./ClienteFacturasVencidasSection";
import { AuditTimeline } from "@/features/audit/AuditTimeline";
import { ClienteFacturasTab } from "@/features/facturas/ClienteFacturasTab";
import { EstadoCuentaSection } from "@/features/facturas/EstadoCuentaSection";
import type { EstadoAgregado, EstatusCliente } from "./schemas";
// rc20+: chip sub-empresa
import { EmpresaChip } from "@/features/empresas/EmpresaChip";

const ESTATUS_VARIANT: Record<EstatusCliente, "success" | "warning" | "destructive" | "secondary"> = {
  activo: "success",
  suspendido: "warning",
  cancelado: "destructive",
  prospecto: "secondary",
};

const AGREGADO_META: Record<
  EstadoAgregado,
  { variant: "success" | "warning" | "destructive" | "secondary"; icon: typeof CheckCircle2; label: string }
> = {
  activo: { variant: "success", icon: CheckCircle2, label: "Servicio activo" },
  suspendido: { variant: "warning", icon: PauseCircle, label: "Suspendido" },
  cancelado: { variant: "destructive", icon: XCircle, label: "Cancelado" },
  error: { variant: "destructive", icon: AlertTriangle, label: "Con error" },
  sin_servicio: { variant: "secondary", icon: XCircle, label: "Sin servicio" },
};

export function ClienteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: cliente, isLoading, isError } = useCliente(id ?? null);
  const { data: resumen } = useClienteResumen(id ?? null);
  const [tab, setTab] = useState("general");
  const [editOpen, setEditOpen] = useState(false);
  const [bajaOpen, setBajaOpen] = useState(false);

  // rc34: gate de botones admin por permiso. Rol "cajero" tiene clientes.read
  // pero NO clientes.write ni clientes.delete → no debe poder editar ni dar
  // de baja al cliente desde aquí.
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEditCliente = hasPermission("clientes.write");
  const canDarBajaCliente = hasPermission("clientes.delete");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (isError || !cliente) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <User size={32} className="text-muted-foreground" />
        <h2 className="text-base font-semibold">Cliente no encontrado</h2>
        <p className="text-xs text-muted-foreground">
          El cliente solicitado no existe o fue eliminado.
        </p>
        <Button size="sm" variant="outline" onClick={() => navigate("/clientes")}>
          <ArrowLeft size={14} /> Volver a clientes
        </Button>
      </div>
    );
  }

  const agregado = resumen ? AGREGADO_META[resumen.estado_agregado] : null;
  const AgregadoIcon = agregado?.icon;
  const vencidasCount = resumen?.facturas_vencidas_count ?? 0;
  const vencidasMonto = resumen ? Number(resumen.facturas_vencidas_monto) : 0;
  const saldoTotal = resumen ? Number(resumen.saldo_pendiente_total) : 0;

  const scrollToVencidas = () => {
    const el = document.getElementById("facturas-vencidas");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumbs + acciones */}
      {/* rc138 — móvil: las acciones bajan a su propia fila */}
      <div className="flex items-start justify-between gap-4 max-md:flex-wrap">
        <div className="flex flex-col gap-1 max-md:min-w-0">
          <Link
            to="/clientes"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-fit"
          >
            <ArrowLeft size={12} />
            Clientes
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{cliente.razon_social}</h1>
            <Badge variant={ESTATUS_VARIANT[cliente.estatus]}>{cliente.estatus}</Badge>
            <Badge variant="outline">{cliente.tipo}</Badge>
            <EmpresaChip empresaId={(cliente as { empresa_id?: number }).empresa_id} size="md" />
            {agregado && AgregadoIcon && (
              <Badge
                variant={agregado.variant}
                className="inline-flex items-center gap-1 px-2 py-0.5"
              >
                <AgregadoIcon size={10} />
                {agregado.label}
                {resumen && resumen.contratos_total > 0 && (
                  <span className="font-normal opacity-80">
                    · {resumen.contratos_activos}/{resumen.contratos_total} activos
                  </span>
                )}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground max-md:flex-wrap max-md:gap-x-2">
            <span className="font-mono">{cliente.codigo}</span>
            <span>·</span>
            <span className="font-mono">{cliente.rfc}</span>
            {cliente.nombre_comercial && (
              <>
                <span>·</span>
                <span>{cliente.nombre_comercial}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cliente.estatus !== "cancelado" && canEditCliente && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil size={12} /> Editar
            </Button>
          )}
          {cliente.estatus !== "cancelado" && canDarBajaCliente && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setBajaOpen(true)}
              title="Dar de baja al cliente con flujo cascada"
            >
              <Ban size={12} /> Dar de baja
            </Button>
          )}
        </div>
      </div>

      {/* Strip de resumen operativo — rc138: 3 columnas también en móvil (texto largo oculto) */}
      {resumen && (
        <div className="grid grid-cols-3 gap-2 max-md:gap-1.5">
          {/* Contratos */}
          <button
            type="button"
            onClick={() => setTab("contratos")}
            className="flex flex-col items-start gap-1 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-accent"
          >
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Contratos
            </span>
            <span className="text-lg font-semibold leading-none">
              {resumen.contratos_total}
            </span>
            <span className="text-[10px] text-muted-foreground max-md:hidden">
              {resumen.contratos_activos} activos · {resumen.contratos_suspendidos}{" "}
              suspendidos
              {resumen.contratos_pendientes > 0 && (
                <> · {resumen.contratos_pendientes} pendientes</>
              )}
            </span>
          </button>

          {/* Facturas vencidas */}
          <button
            type="button"
            onClick={scrollToVencidas}
            disabled={vencidasCount === 0}
            className={cn(
              "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors",
              vencidasCount > 0
                ? "border-destructive/40 bg-destructive/5 hover:bg-destructive/10"
                : "bg-card text-muted-foreground cursor-default"
            )}
          >
            <span className="text-[10px] uppercase tracking-wide">Facturas vencidas</span>
            <span
              className={cn(
                "text-lg font-semibold leading-none",
                vencidasCount > 0 && "text-destructive"
              )}
            >
              {vencidasCount}
            </span>
            <span className="text-[10px]">
              {vencidasCount > 0 ? formatMXN(vencidasMonto) : <><span className="max-md:hidden">Sin adeudos vencidos</span><span className="md:hidden">Al corriente</span></>}
            </span>
          </button>

          {/* Saldo total */}
          <div className="flex flex-col items-start gap-1 rounded-lg border bg-card p-3">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Saldo total
            </span>
            <span className="text-lg font-semibold leading-none max-md:text-sm">
              {formatMXN(saldoTotal)}
            </span>
            <span className="text-[10px] text-muted-foreground max-md:hidden">
              Pendiente (todas las facturas no anuladas)
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="general">
            <User size={12} className="mr-1" />
            General
          </TabsTrigger>
          <TabsTrigger value="contratos">
            <FileText size={12} className="mr-1" />
            Contratos
            {resumen && resumen.contratos_total > 0 && (
              <span className="ml-1 rounded bg-muted px-1 text-[10px]">
                {resumen.contratos_total}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="facturas">
            <Wallet size={12} className="mr-1" />
            Facturas
            {vencidasCount > 0 && (
              <span className="ml-1 rounded bg-destructive/20 px-1 text-[10px] text-destructive">
                {vencidasCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pagos">
            <Receipt size={12} className="mr-1" />
            Pagos
          </TabsTrigger>
          <TabsTrigger value="estado-cuenta">
            <AlarmClock size={12} className="mr-1" />
            Estado de cuenta
          </TabsTrigger>
          <TabsTrigger value="tickets">
            <Ticket size={12} className="mr-1" />
            Tickets
          </TabsTrigger>
          <TabsTrigger value="actividad">
            <Activity size={12} className="mr-1" />
            Actividad
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <ClienteGeneralTab cliente={cliente} />
          {/* Sección de facturas vencidas (ancla del badge) */}
          {vencidasCount > 0 && resumen && (
            <div id="facturas-vencidas" className="mt-4">
              <ClienteFacturasVencidasSection
                items={resumen.facturas_vencidas}
                total={resumen.facturas_vencidas_count}
                monto={vencidasMonto}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="contratos">
          <ClienteContratosTab clienteId={cliente.id} fromLabel={cliente.razon_social} />
        </TabsContent>

        <TabsContent value="facturas">
          <ClienteFacturasTab clienteId={cliente.id} fromLabel={cliente.razon_social} />
        </TabsContent>

        <TabsContent value="pagos">
          <PagosTable
            filters={{ cliente_id: cliente.id, incluir_anulados: true, size: 50 }}
            hideClienteColumn
          />
        </TabsContent>

        <TabsContent value="estado-cuenta">
          <EstadoCuentaSection
            clienteId={cliente.id}
            fromLabel={cliente.razon_social}
          />
        </TabsContent>

        <TabsContent value="tickets">
          <ComingInPhaseTab
            icon={Ticket}
            title="Tickets de soporte"
            phase="v1.1"
            description="El módulo de tickets no forma parte del alcance v1.0. En v1.1 se integrará con un sistema de tickets propio o externo (Freshdesk/Zendesk)."
          />
        </TabsContent>

        <TabsContent value="actividad">
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <Activity size={14} className="text-muted-foreground" />
              <h3 className="text-sm font-semibold">Historial de actividad</h3>
            </div>
            <p className="mb-3 text-[11px] text-muted-foreground">
              Eventos de auditoría sobre este cliente: creación y ediciones. Los
              cambios y acciones sobre sus contratos se ven dentro de cada contrato.
            </p>
            <AuditTimeline entidad="cliente" entidadId={cliente.id} />
          </div>
        </TabsContent>
      </Tabs>

      <ClienteFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        cliente={cliente}
        onSaved={() => setEditOpen(false)}
      />

      {bajaOpen && (
        <DarDeBajaDialog
          clienteId={cliente.id}
          clienteNombre={cliente.razon_social}
          onClose={() => setBajaOpen(false)}
          onSuccess={() => setBajaOpen(false)}
        />
      )}
    </div>
  );
}
